"use client";

import { ChevronDownIcon, LogOutIcon, MoonIcon, SunIcon } from "lucide-react";
import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../atoms";
import { useIsMobile, useTheme } from "../hooks";
import { cn } from "../lib/utils";
import { UserAvatar } from "./user-avatar";

export type UserDropdownProps = React.ComponentProps<"button"> & {
  user: {
    name?: string | null;
    email?: string | null;
    image?: string | null;
  };
  navLinks?: {
    label: string;
    href: string;
    icon?: React.ElementType;
  }[];
  link?: React.ElementType<{ href: string; className?: string }>;
  onSignOut?: () => void;
  // Optional subdued content rendered at the very bottom of the menu, below
  // sign-out. The package stays agnostic about what this is; consumers use it
  // for build/version info and the like.
  footer?: React.ReactNode;
};

function UserMenuTrigger({
  className,
  user,
  showChevron = true,
  ...rest
}: Pick<UserDropdownProps, "className" | "user"> &
  React.ComponentProps<"button"> & { showChevron?: boolean }) {
  return (
    <button
      className={cn("flex items-center gap-1 cursor-pointer", className)}
      {...rest}
    >
      <UserAvatar user={user} />
      {showChevron && (
        <ChevronDownIcon className="size-4 text-text-secondary" />
      )}
    </button>
  );
}

function useThemeToggle() {
  const { isDarkMode, setThemePreference } = useTheme();

  return {
    Icon: isDarkMode ? SunIcon : MoonIcon,
    label: isDarkMode ? "Light Mode" : "Dark Mode",
    onToggle: () => setThemePreference(isDarkMode ? "light" : "dark"),
  };
}

function SignOutConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm?: () => void;
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Sign out?</AlertDialogTitle>
          <AlertDialogDescription>
            You'll need to sign in again to get back into your account.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>Sign out</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function UserDropdown(props: UserDropdownProps) {
  const isMobile = useIsMobile();

  return isMobile ? (
    <UserMenuSheet {...props} />
  ) : (
    <UserMenuPopover {...props} />
  );
}

function UserMenuPopover({
  className,
  user,
  navLinks = [],
  link: Link,
  onSignOut,
  footer,
  ...rest
}: UserDropdownProps) {
  const { Icon: ThemeIcon, label: themeLabel, onToggle } = useThemeToggle();
  const [confirmingSignOut, setConfirmingSignOut] = useState(false);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <UserMenuTrigger className={className} user={user} {...rest} />
          }
        />
        <DropdownMenuContent className="min-w-64" align="end">
          <DropdownMenuLabel className="flex flex-col text-sm font-normal">
            <span>{user?.name}</span>
            <span className="text-xs text-muted-foreground font-normal">
              {user?.email}
            </span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {Link && navLinks.length > 0 && (
            <>
              {navLinks.map(({ label, href, icon: Icon }) => (
                <DropdownMenuItem
                  key={href}
                  nativeButton={false}
                  render={<Link href={href} />}
                >
                  {Icon && <Icon className="size-4" />}
                  {label}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem onClick={onToggle}>
            <ThemeIcon className="size-4" />
            {themeLabel}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setConfirmingSignOut(true)}>
            <LogOutIcon className="size-4" />
            Sign out
          </DropdownMenuItem>
          {footer && (
            <>
              <DropdownMenuSeparator />
              <div className="px-2 py-1.5">{footer}</div>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <SignOutConfirmDialog
        open={confirmingSignOut}
        onOpenChange={setConfirmingSignOut}
        onConfirm={onSignOut}
      />
    </>
  );
}

function UserMenuSheet({
  className,
  user,
  navLinks = [],
  link: Link,
  onSignOut,
  footer,
  ...rest
}: UserDropdownProps) {
  const { Icon: ThemeIcon, label: themeLabel, onToggle } = useThemeToggle();
  const [confirmingSignOut, setConfirmingSignOut] = useState(false);

  return (
    <>
      <Sheet>
        <SheetTrigger
          render={
            <UserMenuTrigger
              className={className}
              user={user}
              showChevron={false}
              {...rest}
            />
          }
        />
        <SheetContent side="bottom" className="gap-0 rounded-t-xl pb-sa-6">
          <SheetHeader>
            <SheetTitle className="text-sm font-normal">
              {user?.name}
            </SheetTitle>
            <span className="text-xs text-muted-foreground">{user?.email}</span>
          </SheetHeader>
          <div className="flex flex-col gap-2 px-4 pt-4">
            {Link &&
              navLinks.map(({ label, href, icon: Icon }) => (
                <SheetClose
                  key={href}
                  nativeButton={false}
                  render={
                    <Link
                      href={href}
                      className="hover:bg-accent flex items-center gap-2 rounded-md px-2 py-2 text-sm [&_svg]:text-muted-foreground"
                    />
                  }
                >
                  {Icon && <Icon className="size-4" />}
                  {label}
                </SheetClose>
              ))}
            <SheetClose
              render={
                <button
                  type="button"
                  onClick={onToggle}
                  className="hover:bg-accent flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm [&_svg]:text-muted-foreground"
                />
              }
            >
              <ThemeIcon className="size-4" />
              {themeLabel}
            </SheetClose>
            <SheetClose
              render={
                <button
                  type="button"
                  onClick={() => setConfirmingSignOut(true)}
                  className="hover:bg-accent flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm [&_svg]:text-muted-foreground"
                />
              }
            >
              <LogOutIcon className="size-4" />
              Sign out
            </SheetClose>
            {footer && <div className="px-2 pt-3">{footer}</div>}
          </div>
        </SheetContent>
      </Sheet>
      <SignOutConfirmDialog
        open={confirmingSignOut}
        onOpenChange={setConfirmingSignOut}
        onConfirm={onSignOut}
      />
    </>
  );
}
